import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { t } from 'i18next';
import ChevronRight from '@/assets/Marketing/ChevronRight.svg';
import { cnMerge } from '@/utils/cnMerge';
import RadixInput from './RadixInput';
import RadixSpinner from './RadixSpinner';

export interface RadixAutocompleteOption {
    value: string;
    label: React.ReactNode | string;
    searchText?: string;
    disabled?: boolean;
}

export interface RadixAutocompleteProps {
    options: RadixAutocompleteOption[];
    value?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    disabled?: boolean;
    isLoading?: boolean;
    className?: string;
    triggerClassName?: string;
    contentClassName?: string;
    onValueChange?: (value: string) => void;
}

const RadixAutocomplete: React.FC<RadixAutocompleteProps> = ({
    options,
    value,
    placeholder = t('Common.Select'),
    searchPlaceholder = t('Common.Search'),
    emptyText = t('Components.NoData'),
    disabled = false,
    isLoading = false,
    className,
    triggerClassName,
    contentClassName,
    onValueChange,
}) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');

    const selectedOption = React.useMemo(() => options.find((option) => option.value === value), [options, value]);

    const normalizedQuery = query.trim().toLowerCase();
    const filteredOptions = React.useMemo(() => {
        if (!normalizedQuery) return options;

        return options.filter((option) => {
            const searchBase = (typeof option.label === 'string' ? option.label : '') + ' ' + (option.searchText ?? '');
            return searchBase.toLowerCase().includes(normalizedQuery);
        });
    }, [options, normalizedQuery]);

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button
                    type="button"
                    disabled={disabled}
                    className={cnMerge(
                        'w-full h-10',
                        'cursor-pointer group inline-flex items-center justify-between',
                        'px-4 py-2 rounded-md',
                        'border-[1px] border-solid border-border-default',
                        'bg-transparent text-text-primary shadow-sm',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        className,
                        triggerClassName,
                    )}
                >
                    <span className="truncate text-left">{selectedOption?.label ?? placeholder}</span>
                    {isLoading ? (
                        <span className="mx-2 flex items-center gap-2">
                            <RadixSpinner size="sm" variant="primary" className="inline-flex" />
                        </span>
                    ) : (
                        <img
                            src={ChevronRight}
                            alt="Chevron Right"
                            className="w-4 h-4 transition-transform duration-200 rotate-90 group-data-[state=open]:rotate-[270deg] filter brightness-0"
                        />
                    )}
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    align="start"
                    sideOffset={6}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    className={cnMerge(
                        'z-[10000] w-[var(--radix-popover-trigger-width)] rounded-md border border-border-default',
                        'bg-background-paper shadow-lg p-2',
                        'custom-scroll',
                        contentClassName,
                    )}
                >
                    <RadixInput
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={searchPlaceholder}
                        className="h-9"
                    />
                    <div
                        className="mt-2 h-[260px] overflow-y-auto overscroll-contain"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                        onWheel={(e) => e.stopPropagation()}
                    >
                        {isLoading ? (
                            <div className="py-3 flex items-center justify-center">
                                <RadixSpinner size="sm" variant="primary" />
                            </div>
                        ) : filteredOptions.length === 0 ? (
                            <div className="px-2 py-3 text-sm text-text-secondary">{emptyText}</div>
                        ) : (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    disabled={option.disabled}
                                    className={cnMerge(
                                        'border-[1px] border-solid ',
                                        'rounded-md',
                                        'cursor-pointer',
                                        'mt-1',
                                        'w-full text-left px-3 py-2 text-sm',
                                        'text-text-primary',
                                        'disabled:opacity-50 disabled:cursor-not-allowed',
                                        value === option.value
                                            ? 'bg-primary-50 border-primary-500'
                                            : 'bg-transparent border-border-default',
                                    )}
                                    onClick={() => {
                                        onValueChange?.(option.value);
                                        setOpen(false);
                                        setQuery('');
                                    }}
                                >
                                    {option.label}
                                </button>
                            ))
                        )}
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

export default RadixAutocomplete;
